/**
 * 사용자별 localStorage 키 관리 유틸리티
 */

// 현재 로그인한 사용자 정보를 가져오는 함수
export const getCurrentUserId = () => {
  try {
    // 1. Redux persist로 저장된 auth 정보에서 사용자 ID 추출 (usrid 우선)
    const persistedAuth = localStorage.getItem('persist:auth');
    if (persistedAuth) {
      const authData = JSON.parse(persistedAuth);
      const user = JSON.parse(authData.user || 'null');
      if (user && (user.usrid || user.id || user.username)) {
        return user.usrid || user.id || user.username;
      }
    }
    
    // 2. 기존 livesql_user에서 확인 (fallback, usrid 우선)
    const livesqlUser = localStorage.getItem('livesql_user');
    if (livesqlUser) {
      const user = JSON.parse(livesqlUser);
      if (user && (user.usrid || user.id || user.username)) {
        return user.usrid || user.id || user.username;
      }
    }
    
    // 3. sessionStorage 확인 (추가 fallback, usrid 우선)
    const sessionUser = sessionStorage.getItem('currentUser');
    if (sessionUser) {
      const user = JSON.parse(sessionUser);
      if (user && (user.usrid || user.id || user.username)) {
        return user.usrid || user.id || user.username;
      }
    }
    
    return 'anonymous';
  } catch (error) {
    console.warn('Failed to get current user ID:', error);
    return 'anonymous';
  }
};

// 사용자별 localStorage 키 생성
export const getUserStorageKey = (baseKey, userId = null) => {
  const currentUserId = userId || getCurrentUserId();
  return `user_${currentUserId}_${baseKey}`;
};

// 사용자별 데이터 저장
export const setUserData = (key, data, userId = null) => {
  try {
    const userKey = getUserStorageKey(key, userId);
    localStorage.setItem(userKey, JSON.stringify(data));
    console.log(`User data saved: ${userKey}`);
    return true;
  } catch (error) {
    console.warn(`Failed to save user data for key ${key}:`, error);
    return false;
  }
};

// 사용자별 데이터 조회
export const getUserData = (key, defaultValue = null, userId = null) => {
  try {
    const userKey = getUserStorageKey(key, userId);
    const data = localStorage.getItem(userKey);
    return data ? JSON.parse(data) : defaultValue;
  } catch (error) {
    console.warn(`Failed to get user data for key ${key}:`, error);
    return defaultValue;
  }
};

// 사용자별 데이터 삭제
export const removeUserData = (key, userId = null) => {
  try {
    const userKey = getUserStorageKey(key, userId);
    localStorage.removeItem(userKey);
    console.log(`User data removed: ${userKey}`);
    return true;
  } catch (error) {
    console.warn(`Failed to remove user data for key ${key}:`, error);
    return false;
  }
};

// 특정 사용자의 모든 데이터 삭제 (로그아웃 시 사용)
export const clearUserData = (userId = null) => {
  try {
    const currentUserId = userId || getCurrentUserId();
    const userPrefix = `user_${currentUserId}_`;
    
    // localStorage에서 해당 사용자의 모든 키 찾아서 삭제
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(userPrefix)) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      console.log(`Cleared user data: ${key}`);
    });
    
    return keysToRemove.length;
  } catch (error) {
    console.warn('Failed to clear user data:', error);
    return 0;
  }
};

// 이전 사용자 데이터 정리 (옵션)
export const cleanupOldUserData = (keepRecentUsers = 3) => {
  try {
    // 모든 사용자별 키를 찾아서 최근 사용하지 않은 데이터 정리
    const userDataKeys = {};
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('user_') && key.includes('_')) {
        const userIdMatch = key.match(/^user_([^_]+)_/);
        if (userIdMatch) {
          const userId = userIdMatch[1];
          if (!userDataKeys[userId]) {
            userDataKeys[userId] = [];
          }
          userDataKeys[userId].push(key);
        }
      }
    }
    
    const userIds = Object.keys(userDataKeys);
    if (userIds.length > keepRecentUsers) {
      // 오래된 사용자 데이터 정리 (간단한 구현)
      const usersToRemove = userIds.slice(0, userIds.length - keepRecentUsers);
      
      usersToRemove.forEach(userId => {
        userDataKeys[userId].forEach(key => {
          localStorage.removeItem(key);
          console.log(`Cleaned up old user data: ${key}`);
        });
      });
      
      return usersToRemove.length;
    }
    
    return 0;
  } catch (error) {
    console.warn('Failed to cleanup old user data:', error);
    return 0;
  }
};

// 기존 전역 데이터를 사용자별 데이터로 마이그레이션 (최초 로그인 시 호출)
export const migrateGlobalDataToUser = (userId = null) => {
  try {
    const currentUserId = userId || getCurrentUserId();
    if (currentUserId === 'anonymous') {
      return 0;
    }
    
    // 마이그레이션할 키들
    const keysToMigrate = [
      'editor_state',
      'persist:schema', 
      'persist:history'
    ];
    
    let migratedCount = 0;
    
    keysToMigrate.forEach(key => {
      const globalData = localStorage.getItem(key);
      if (globalData) {
        const userKey = getUserStorageKey(key.replace('persist:', 'persist_'), currentUserId);
        
        // 사용자별 키가 이미 존재하지 않는 경우에만 마이그레이션
        if (!localStorage.getItem(userKey)) {
          localStorage.setItem(userKey, globalData);
          console.log(`Migrated global data: ${key} → ${userKey}`);
          migratedCount++;
        }
        
        // 전역 키 삭제 (auth는 제외)
        if (!key.includes('auth')) {
          localStorage.removeItem(key);
          console.log(`Removed global key: ${key}`);
        }
      }
    });
    
    return migratedCount;
  } catch (error) {
    console.warn('Failed to migrate global data to user:', error);
    return 0;
  }
};

export default {
  getCurrentUserId,
  getUserStorageKey,
  setUserData,
  getUserData,
  removeUserData,
  clearUserData,
  cleanupOldUserData,
  migrateGlobalDataToUser
};